import React, { useRef } from 'react';
import { useFormikContext } from 'formik';

import Input from '@components/form/Input';
import SociollaErrorMessage from './SociollaErrorMessage';

import { isVn } from '@global/helpers';
import { isAndroid } from '@global/screens';

import PropTypes from 'prop-types';

const propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  isNPWP: PropTypes.bool,
  isKTP: PropTypes.bool,
  isUppercase: PropTypes.bool,
};

const formatNPWPValue = (value) => {
  if (!value) {
    return '';
  }

  if (!isVn()) {
    value = value.replace(/\D/g, '');

    if (value.length > 16) {
      value = value.slice(0, 16);
    }
  }

  return value;
};

// eslint-disable-next-line react/prop-types
function SociollaInput({
  name,
  onChange,
  isNPWP = false,
  isKTP = false,
  isUppercase = false,
  ...otherProps
}) {
  const { setFieldTouched, setFieldValue, errors, touched, values } =
    useFormikContext();
  const inputRef = useRef(null);

  const handleChange = async (value) => {
    let formattedValue = value;
    let rawValue = value;

    if (isNPWP) {
      if (isVn()) {
        rawValue = value;
        formattedValue = value;
      } else {
        rawValue = value.replace(/\D/g, '').slice(0, 16);
        formattedValue = rawValue;
      }
    }

    if (isKTP) {
      rawValue = isVn()
        ? value.replace(/\D/g, '')
        : value.replace(/\D/g, '').slice(0, 16);
      formattedValue = rawValue;
    }

    // Native: autoCapitalize="characters" makes the keyboard emit uppercase, so
    // RN never re-sets the text and the caret stays put. Browsers ignore that
    // attribute, so on web we transform here and restore the caret via the DOM.
    let cursorPos = null;
    if (isUppercase) {
      const domSelectionStart = inputRef.current?.input?.selectionStart;
      if (typeof domSelectionStart === 'number') {
        cursorPos = domSelectionStart;
      }
      rawValue = rawValue.toUpperCase();
      formattedValue = rawValue;
    }

    await setFieldValue(name, rawValue);
    if (onChange) {
      onChange(rawValue);
    }

    setFieldValue(`${name}Formatted`, formattedValue);

    if (cursorPos != null) {
      requestAnimationFrame(() => {
        const node = inputRef.current?.input;
        if (node && typeof node.setSelectionRange === 'function') {
          node.setSelectionRange(cursorPos, cursorPos);
        }
      });
    }
  };

  return (
    <>
      <Input
        inputRef={inputRef}
        onBlur={() => setFieldTouched(name)}
        onChangeText={handleChange}
        value={isNPWP ? formatNPWPValue(values[name]) : values[name]}
        autoCapitalize={isUppercase ? 'characters' : undefined}
        autoCorrect={isUppercase && isAndroid ? true : undefined}
        {...otherProps}
      />
      <SociollaErrorMessage error={errors[name]} visible={touched[name]} />
    </>
  );
}

SociollaInput.propTypes = propTypes;

export default SociollaInput;
